// __EQUIP_ASSIGN__ (2026-10-04) side-view silhouettes of each kind of unit for the Equipment
// Assignment view (Henry: "each equipment is depicted as an image representing its general shape
// and appearance"). Inline SVG, drawn for the dark board. Trucks face right; a trailer's hitch is
// at its right end, so a coupled pair reads [trailer][truck].
// Details follow the unit's features: ramps, crane, tarps.
import React from 'react';
import { trailerHitch } from '../../utils/equipmentCompat';

const C = {
  cab: '#2f6fb3', cabEdge: '#79b8ff', glass: '#a5d6ff', body: '#3a424c', bodyEdge: '#8b949e',
  deck: '#7a5c3a', deckEdge: '#b08a5a', frame: '#1c2128', wheel: '#0d1117', tire: '#6e7681', hub: '#c9d1d9',
  curtain: '#35557a', tarp: '#2f7d47', tarpEdge: '#56d364', crane: '#d29922', craneEdge: '#f2cc60',
  tank: '#8b949e', tankEdge: '#d0d7de', dump: '#9a6700', dumpEdge: '#d29922', chrome: '#c9d1d9', ramp: '#d29922',
  steel: '#57606a',
};
const has = (u, f) => Array.isArray(u && u.features) && u.features.includes(f);

const Wheel = ({ x, y = 49, r = 6.5 }) => (
  <g>
    <circle cx={x} cy={y} r={r} fill={C.wheel} stroke={C.tire} strokeWidth="2" />
    <circle cx={x} cy={y} r={r * 0.35} fill={C.hub} />
  </g>
);
const Ground = ({ w }) => <line x1="0" y1="56" x2={w} y2="56" stroke="#30363d" strokeWidth="1" strokeDasharray="3 3" />;

// ---------------------------------------------------------------- truck parts
const DayCab = ({ x = 96, tall = false }) => (
  <g>
    <path d={`M${x} 44 L${x} ${tall ? 12 : 16} Q${x} ${tall ? 10 : 14} ${x + 3} ${tall ? 10 : 14} L${x + 18} ${tall ? 10 : 14} L${x + 22} 28 L${x + 32} 30 Q${x + 34} 31 ${x + 34} 34 L${x + 34} 44 Z`}
      fill={C.cab} stroke={C.cabEdge} strokeWidth="1" />
    <path d={`M${x + 6} ${tall ? 14 : 18} L${x + 17} ${tall ? 14 : 18} L${x + 20} 27 L${x + 6} 27 Z`} fill={C.glass} opacity="0.75" />
    <rect x={x + 30} y="35" width="4" height="5" fill={C.chrome} opacity="0.8" />
  </g>
);
const Frame = ({ x1 = 6, x2 = 126, y = 42 }) => <rect x={x1} y={y} width={x2 - x1} height="4" fill={C.frame} stroke="#484f58" strokeWidth="0.6" />;
const Ramps = ({ x = 2, y = 38 }) => (
  <g stroke={C.ramp} strokeWidth="2" strokeLinecap="round">
    <line x1={x + 2} y1={y} x2={x + 7} y2={y - 20} />
    <line x1={x + 6} y1={y} x2={x + 11} y2={y - 20} />
  </g>
);
const CraneArm = ({ x = 84 }) => (
  <g>
    <rect x={x} y="24" width="7" height="16" fill={C.crane} stroke={C.craneEdge} strokeWidth="0.8" />
    <path d={`M${x + 3} 26 L${x - 46} 14 L${x - 48} 18 L${x + 3} 31 Z`} fill={C.crane} stroke={C.craneEdge} strokeWidth="0.8" />
    <line x1={x - 46} y1="17" x2={x - 46} y2="26" stroke={C.chrome} strokeWidth="1" />
    <path d={`M${x - 48} 26 q2 4 4 0`} fill="none" stroke={C.chrome} strokeWidth="1.2" />
  </g>
);

// body on a straight truck (x 6..92)
function StraightBody({ type, unit }) {
  switch (type) {
    case 'box_truck':
      return (<g><rect x="6" y="8" width="86" height="34" rx="2" fill={C.body} stroke={C.bodyEdge} />
        {[14, 20, 26, 32].map((y) => <line key={y} x1="8" y1={y} x2="14" y2={y} stroke={C.bodyEdge} strokeWidth="0.8" />)}</g>);
    case 'curtain_side':
      return (<g><rect x="6" y="8" width="86" height="34" rx="2" fill={C.curtain} stroke={C.bodyEdge} />
        {[16, 26, 36, 46, 56, 66, 76, 86].map((x) => <line key={x} x1={x} y1="10" x2={x} y2="40" stroke="#8fb3d9" strokeWidth="1" opacity="0.7" />)}
        <rect x="6" y="8" width="86" height="4" fill={C.body} /></g>);
    case 'roll_tarp':
      return (<g><path d="M6 42 L6 18 Q6 8 18 8 L80 8 Q92 8 92 18 L92 42 Z" fill={C.tarp} stroke={C.tarpEdge} strokeWidth="1" />
        {[20, 34, 48, 62, 76].map((x) => <line key={x} x1={x} y1="9" x2={x} y2="41" stroke={C.tarpEdge} strokeWidth="0.7" opacity="0.6" />)}
        <rect x="6" y="38" width="86" height="4" fill={C.deck} /></g>);
    case 'dump_truck':
      return (<path d="M6 42 L10 16 L88 16 L92 42 Z" fill={C.dump} stroke={C.dumpEdge} strokeWidth="1" />);
    case 'tanker':
      return (<g><rect x="8" y="14" width="82" height="26" rx="13" fill={C.tank} stroke={C.tankEdge} />
        <line x1="10" y1="22" x2="88" y2="22" stroke={C.tankEdge} strokeWidth="0.6" opacity="0.6" /></g>);
    case 'tilt_deck':
      return (<g><path d="M4 44 L92 34 L92 38 L6 46 Z" fill={C.deck} stroke={C.deckEdge} strokeWidth="1" />
        <line x1="90" y1="34" x2="90" y2="26" stroke={C.steel} strokeWidth="2" /></g>);
    default: // flatbed / straight_truck / crane_truck / other
      return (<g><rect x="6" y="36" width="86" height="6" fill={C.deck} stroke={C.deckEdge} strokeWidth="1" />
        {type !== 'crane_truck' && [12, 30, 48, 66].map((x) => <line key={x} x1={x} y1="30" x2={x} y2="36" stroke={C.deckEdge} strokeWidth="1.5" />)}
        {has(unit, 'tarps') && <path d="M10 36 Q48 20 88 36 Z" fill={C.tarp} opacity="0.85" />}</g>);
  }
}

// ---------------------------------------------------------------- trucks
export function TruckArt({ unit, width = 150, title }) {
  const type = (unit && unit.truck_type) || 'other';
  let art;
  if (type === 'day_cab' || type === 'sleeper') {
    const sleeper = type === 'sleeper';
    art = (
      <g>
        <Frame x1={6} x2={126} />
        {sleeper && <rect x="74" y="16" width="22" height="28" rx="2" fill={C.cab} stroke={C.cabEdge} />}
        <DayCab x={92} tall />
        <rect x="16" y="38" width="26" height="4" rx="1" fill={C.steel} stroke={C.chrome} strokeWidth="0.6" />
        <line x1={sleeper ? 72 : 88} y1="6" x2={sleeper ? 72 : 88} y2="40" stroke={C.chrome} strokeWidth="2" />
        <Wheel x={20} /><Wheel x={36} /><Wheel x={114} />
      </g>
    );
  } else if (type === 'pickup' || type === 'hotshot') {
    art = (
      <g>
        <path d="M12 44 L12 28 L72 28 L74 18 Q75 16 78 16 L100 16 L110 27 L124 29 Q127 30 127 33 L127 44 Z" fill={C.cab} stroke={C.cabEdge} />
        <path d="M80 19 L98 19 L106 27 L80 27 Z" fill={C.glass} opacity="0.75" />
        <line x1="72" y1="28" x2="72" y2="44" stroke={C.cabEdge} strokeWidth="0.8" />
        {type === 'hotshot' && <g><circle cx="42" cy="27" r="2.5" fill={C.chrome} /><line x1="42" y1="27" x2="42" y2="30" stroke={C.chrome} strokeWidth="2" /></g>}
        <rect x="6" y="37" width="6" height="3" fill={C.chrome} />
        <Wheel x={30} /><Wheel x={108} />
        {type === 'hotshot' && <circle cx="30" cy="49" r="7.6" fill="none" stroke={C.tire} strokeWidth="0.8" opacity="0.6" />}
      </g>
    );
  } else {
    art = (
      <g>
        <Frame x1={6} x2={126} />
        <StraightBody type={type} unit={unit} />
        <DayCab x={94} />
        {(type === 'crane_truck' || has(unit, 'crane')) && <CraneArm x={type === 'crane_truck' ? 86 : 84} />}
        {has(unit, 'ramps') && <Ramps x={0} y={36} />}
        <Wheel x={22} /><Wheel x={38} /><Wheel x={114} />
      </g>
    );
  }
  return (
    <svg viewBox="0 0 130 60" width={width} height={width * 60 / 130} role="img" aria-label={title || type} style={{ display: 'block', overflow: 'visible' }}>
      {title && <title>{title}</title>}
      <Ground w={130} />
      {art}
    </svg>
  );
}

// ---------------------------------------------------------------- trailers
export function TrailerArt({ unit, width = 190, title }) {
  const type = (unit && unit.trailer_type) || 'other';
  const ramps = has(unit, 'ramps');
  let art;
  switch (type) {
    case 'gooseneck':
      art = (<g>
        <rect x="6" y="38" width="128" height="6" fill={C.deck} stroke={C.deckEdge} />
        <path d="M128 38 L136 22 L168 22 L168 30 L140 30 L134 44 L128 44 Z" fill={C.steel} stroke={C.chrome} strokeWidth="0.8" />
        <rect x="163" y="30" width="4" height="5" fill={C.chrome} />
        <line x1="140" y1="30" x2="140" y2="50" stroke={C.steel} strokeWidth="2" />
        {[20, 50, 80, 110].map((x) => <line key={x} x1={x} y1="38" x2={x} y2="35" stroke={C.deckEdge} strokeWidth="1.2" />)}
        {ramps && <Ramps x={2} y={38} />}
        <Wheel x={34} /><Wheel x={50} /><Wheel x={66} />
      </g>);
      break;
    case 'step_deck':
      art = (<g>
        <rect x="6" y="38" width="118" height="6" fill={C.deck} stroke={C.deckEdge} />
        <path d="M122 44 L122 30 L168 30 L168 36 L128 36 L128 44 Z" fill={C.deck} stroke={C.deckEdge} />
        <rect x="150" y="36" width="5" height="4" fill={C.chrome} /><line x1="132" y1="36" x2="132" y2="52" stroke={C.steel} strokeWidth="2" />
        {ramps && <Ramps x={2} y={38} />}
        <Wheel x={22} /><Wheel x={38} />
      </g>);
      break;
    case 'lowboy':
      art = (<g>
        <path d="M4 36 L34 36 L40 44 L126 44 L132 34 L168 30 L168 36 L136 38 L130 48 L36 48 L30 42 L4 42 Z" fill={C.deck} stroke={C.deckEdge} />
        <rect x="160" y="36" width="5" height="4" fill={C.chrome} />
        {ramps && <Ramps x={0} y={36} />}
        <Wheel x={12} y={49} r={5.5} /><Wheel x={26} y={49} r={5.5} />
      </g>);
      break;
    case 'dry_van':
    case 'reefer':
    case 'livestock':
      art = (<g>
        <rect x="4" y="8" width="160" height="34" rx="2" fill={C.body} stroke={C.bodyEdge} />
        {type === 'livestock' && [14, 22, 30].map((y) => <line key={y} x1="10" y1={y} x2="158" y2={y} stroke={C.bodyEdge} strokeWidth="2" strokeDasharray="6 3" opacity="0.7" />)}
        {type === 'reefer' && <rect x="164" y="12" width="5" height="18" fill={C.chrome} stroke={C.bodyEdge} />}
        <line x1="8" y1="10" x2="8" y2="40" stroke={C.bodyEdge} strokeWidth="0.8" />
        <rect x="4" y="42" width="160" height="3" fill={C.frame} />
        <line x1="130" y1="45" x2="130" y2="52" stroke={C.steel} strokeWidth="2" />
        <Wheel x={22} /><Wheel x={38} />
      </g>);
      break;
    case 'tanker':
      art = (<g>
        <rect x="6" y="12" width="156" height="30" rx="15" fill={C.tank} stroke={C.tankEdge} />
        {[50, 100].map((x) => <line key={x} x1={x} y1="13" x2={x} y2="41" stroke={C.tankEdge} strokeWidth="0.8" opacity="0.7" />)}
        <rect x="6" y="42" width="156" height="3" fill={C.frame} /><line x1="130" y1="45" x2="130" y2="52" stroke={C.steel} strokeWidth="2" />
        <Wheel x={22} /><Wheel x={38} />
      </g>);
      break;
    case 'dump':
    case 'hopper':
      art = (<g>
        {type === 'dump'
          ? <path d="M6 42 L12 12 L158 12 L162 42 Z" fill={C.dump} stroke={C.dumpEdge} />
          : <path d="M6 12 L162 12 L162 30 L130 42 L100 30 L70 42 L40 30 L6 30 Z" fill={C.dump} stroke={C.dumpEdge} />}
        <rect x="6" y="42" width="156" height="3" fill={C.frame} /><line x1="134" y1="45" x2="134" y2="52" stroke={C.steel} strokeWidth="2" />
        <Wheel x={22} /><Wheel x={38} />
      </g>);
      break;
    case 'bumper_pull':   // __EQUIP_R5__ bumper-pull deck trailer: dovetail, tandem axle, A-frame tongue to the ball
      art = (<g>
        <path d="M8 40 L18 36 L140 36 L140 42 L18 42 Z" fill={C.deck} stroke={C.deckEdge} />
        {[34, 60, 86, 112].map((x) => <line key={x} x1={x} y1="36" x2={x} y2="33" stroke={C.deckEdge} strokeWidth="1.2" />)}
        <path d="M140 38 L166 40 L166 42 L140 42 Z" fill={C.steel} stroke={C.chrome} strokeWidth="0.6" />
        <circle cx="167" cy="41" r="2.2" fill={C.chrome} />
        <line x1="150" y1="42" x2="150" y2="50" stroke={C.steel} strokeWidth="2" />
        <path d="M64 42 Q78 30 94 42" fill="none" stroke={C.bodyEdge} strokeWidth="1" />
        {ramps && <Ramps x={4} y={38} />}
        <Wheel x={70} /><Wheel x={86} />
      </g>);
      break;
    case 'utility':
    case 'tilt':
    case 'car_hauler':
      art = (<g>
        {type === 'tilt'
          ? <path d="M30 46 L136 38 L136 42 L32 50 Z" fill={C.deck} stroke={C.deckEdge} />
          : <rect x="30" y="38" width="106" height="6" fill={C.deck} stroke={C.deckEdge} />}
        <path d="M136 41 L168 40 L168 42 L136 44 Z" fill={C.steel} stroke={C.chrome} strokeWidth="0.6" />
        <circle cx="168" cy="41" r="2" fill={C.chrome} />
        {type === 'utility' && <g stroke={C.bodyEdge} strokeWidth="1">{[32, 36, 40].map((x) => <line key={x} x1={x} y1="38" x2={x - 6} y2="18" />)}</g>}
        {type === 'car_hauler' && <path d="M54 38 L60 28 L96 28 L106 34 L118 35 L118 38 Z" fill="none" stroke={C.chrome} strokeWidth="1.2" />}
        {ramps && <Ramps x={26} y={38} />}
        <Wheel x={78} /><Wheel x={94} />
      </g>);
      break;
    default: // flatbed, deck_over, other: fifth-wheel flat deck
      art = (<g>
        <rect x="4" y={type === 'deck_over' ? 32 : 36} width="164" height="6" fill={C.deck} stroke={C.deckEdge} />
        {[20, 50, 80, 110, 140].map((x) => <line key={x} x1={x} y1={type === 'deck_over' ? 32 : 36} x2={x} y2={type === 'deck_over' ? 29 : 33} stroke={C.deckEdge} strokeWidth="1.2" />)}
        {has(unit, 'tarps') && <path d="M10 36 Q86 18 162 36 Z" fill={C.tarp} opacity="0.85" />}
        <rect x="150" y="42" width="5" height="3" fill={C.chrome} /><line x1="128" y1="42" x2="128" y2="52" stroke={C.steel} strokeWidth="2" />
        {ramps && <Ramps x={0} y={36} />}
        <Wheel x={22} /><Wheel x={38} />
      </g>);
  }
  return (
    <svg viewBox="0 0 170 60" width={width} height={width * 60 / 170} role="img" aria-label={title || type} style={{ display: 'block', overflow: 'visible' }}>
      {title && <title>{title}</title>}
      <Ground w={170} />
      {art}
    </svg>
  );
}

// how far the trailer's hitch end reaches over the truck: fifth wheel ~x 30, gooseneck ball in a
// pickup bed ~x 44, bumper-pull ball at the rear bumper ~x 10 (__EQUIP_R5__)
export const hitchOverlap = (truck, trailer) => {
  if (trailer && trailerHitch(trailer) === 'bumper') return 12;
  return truck && (truck.truck_type === 'pickup' || truck.truck_type === 'hotshot') ? 48 : 34;
};

// A power unit with its trailer coupled: [trailer][truck], the trailer's hitch over the truck's.
export function CoupledArt({ truck, trailer, scale = 1 }) {
  const tw = 130 * scale; const pw = 170 * scale;
  if (!trailer) return <TruckArt unit={truck} width={tw} />;
  const overlap = hitchOverlap(truck, trailer) * scale;
  return (
    <div style={{ display: 'flex', alignItems: 'flex-end' }}>
      <div style={{ marginRight: -overlap + 'px', position: 'relative', zIndex: 1 }}><TrailerArt unit={trailer} width={pw} /></div>
      <div style={{ position: 'relative', zIndex: 0 }}><TruckArt unit={truck} width={tw} /></div>
    </div>
  );
}
